import { useLayoutEffect, useRef } from 'react';
import type { DialerNotice } from '../lib/dialerDiagnostics';

export type DialerNoticeBannerProps = {
  notice: DialerNotice | null;
  onDismiss?: () => void;
  onRecheck?: () => void;
  /** altura ocupada pela faixa (0 quando some): o discador usa para encolher o teclado e não esconder o botão de ligar */
  onHeightChange?: (height: number) => void;
};

/** Faixa de aviso do discador: título, explicação e o que fazer, no lugar de "nada acontece". */
export function DialerNoticeBanner({ notice, onDismiss, onRecheck, onHeightChange }: DialerNoticeBannerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = notice !== null;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!onHeightChange) return;
    if (!el) {
      onHeightChange(0);
      return;
    }
    const report = () => onHeightChange(el.offsetHeight);
    report();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [visible, onHeightChange]);

  if (!notice) return null;
  const dismissible = notice.source === 'call' || notice.source === 'session';
  return (
    <div ref={ref} className={`dialer-notice ${notice.level}`} role={notice.level === 'error' ? 'alert' : 'status'} data-code={notice.code}>
      <i className={`fas ${notice.level === 'error' ? 'fa-circle-exclamation' : 'fa-triangle-exclamation'} dialer-notice-icon`} aria-hidden="true" />
      <div className="dialer-notice-body">
        <strong className="dialer-notice-title">{notice.title}</strong>
        <span className="dialer-notice-detail">{notice.detail}</span>
        {notice.action && <span className="dialer-notice-action">{notice.action}</span>}
        {notice.source === 'device' && onRecheck && (
          <button type="button" className="dialer-notice-btn" onClick={onRecheck}>
            Verificar de novo
          </button>
        )}
      </div>
      {dismissible && onDismiss && (
        <button type="button" className="dialer-notice-close" onClick={onDismiss} aria-label="Fechar aviso">
          <i className="fas fa-xmark" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
